import { createSelector } from "@reduxjs/toolkit";

import { formatDateToMonthYear } from "Libs/datetime";

import type { RootState } from "Store/configureStore";

interface SelectorProps {
  organizationId: string;
}

const selectCarbonEmissions = (state: RootState) => state.carbonEmissions;
const selectOrgId = (_: RootState, { organizationId }: SelectorProps) =>
  organizationId;

// Basic state selectors
export const selectCarbonEmissionsStateByOrganizationId = createSelector(
  selectCarbonEmissions,
  selectOrgId,
  (state, organizationId) =>
    state.byOrganizationId[organizationId] || {
      data: null,
      isLoading: false,
      error: null,
      isNonRetryableError: false
    }
);

export const selectCarbonEmissionsDataByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.data
);

export const selectCarbonEmissionsIsLoadingByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.isLoading
);

export const selectCarbonEmissionsErrorByOrganizationId = createSelector(
  selectCarbonEmissionsStateByOrganizationId,
  state => state.error
);

export const selectCarbonEmissionsIsNonRetryableErrorByOrganizationId =
  createSelector(
    selectCarbonEmissionsStateByOrganizationId,
    state => state.isNonRetryableError || false
  );

// Projects selector - now using cleaned data structure
export const selectCarbonEmissionsProjects = createSelector(
  selectCarbonEmissionsDataByOrganizationId,
  data => data?.data?.projects || []
);

// Base monthly data extraction - expensive operation that gets memoized
export const selectCarbonEmissionsMonthlyDataBase = createSelector(
  selectCarbonEmissionsProjects,
  projects => {
    const monthlyMap = new Map<string, number>();

    projects.forEach(project => {
      if (project.values && project.values.length > 0) {
        project.values.forEach(value => {
          const month = value.start_time.substring(0, 7); // Get YYYY-MM format
          const currentValue = monthlyMap.get(month) || 0;
          monthlyMap.set(month, currentValue + value.value);
        });
      }
    });

    // Convert map to array and sort by month
    return Array.from(monthlyMap.entries())
      .map(([month, value]) => ({ month, value }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }
);

// Raw monthly data for other potential views
export const selectCarbonEmissionsMonthlyData = createSelector(
  selectCarbonEmissionsMonthlyDataBase,
  monthlyData => monthlyData
);

// Calculate total CO2 for previous year
export const selectCarbonEmissionsPreviousYearTotal = createSelector(
  selectCarbonEmissionsMonthlyDataBase,
  monthlyData => {
    if (!monthlyData || monthlyData.length === 0) {
      return 0;
    }
    return monthlyData.reduce((total, month) => total + month.value, 0);
  }
);

// Format monthly data for chart display - view-specific transformation
export const selectCarbonEmissionsMonthlyDataForChart = createSelector(
  selectCarbonEmissionsMonthlyDataBase,
  (monthlyData): { label: string; value: number }[] => {
    if (!monthlyData || monthlyData.length === 0) {
      return [];
    }

    return monthlyData.map(item => {
      let label: string;
      try {
        const [, month] = item.month.split("-");
        const date = new Date(2000, parseInt(month) - 1, 1);
        label = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
      } catch {
        label = item.month;
      }

      return {
        label,
        value: item.value
      };
    });
  }
);

// Calculate per-project totals for the projects table - view-specific transformation
export const selectCarbonEmissionsProjectsTableData = createSelector(
  selectCarbonEmissionsProjects,
  projects => {
    return projects
      .map(project => {
        const hasValues = project.values && project.values.length > 0;
        const totalEmissions = hasValues
          ? project.values.reduce((sum, value) => sum + value.value, 0)
          : null;

        return {
          projectId: project.project_id,
          projectName: project.project_title || `Project ${project.project_id}`,
          totalEmissions
        };
      })
      .sort((a, b) => {
        if (a.totalEmissions === null && b.totalEmissions !== null) return 1;
        if (a.totalEmissions !== null && b.totalEmissions === null) return -1;
        if (a.totalEmissions === null && b.totalEmissions === null) return 0;
        return (b.totalEmissions || 0) - (a.totalEmissions || 0);
      });
  }
);

// CSV data transformation - business logic for CSV export
export const selectCarbonEmissionsCSVData = createSelector(
  selectCarbonEmissionsProjects,
  selectOrgId,
  (projects, organizationId) => {
    const csvRecords: Array<{
      date: string;
      project_id: string;
      project_title: string;
      organization_id: string;
      total_daily_kgCO2e: number;
    }> = [];

    projects.forEach(project => {
      if (project.values && project.values.length > 0) {
        // Group by month and sum values
        const monthlyTotals = new Map<string, number>();

        project.values.forEach(dataPoint => {
          const monthKey = formatDateToMonthYear(dataPoint.start_time);
          const currentTotal = monthlyTotals.get(monthKey) || 0;
          monthlyTotals.set(monthKey, currentTotal + dataPoint.value);
        });

        // Create CSV records for each month
        monthlyTotals.forEach((totalEmissions, monthKey) => {
          csvRecords.push({
            date: monthKey,
            project_id: project.project_id,
            project_title:
              project.project_title || `Project ${project.project_id}`,
            organization_id: organizationId,
            total_daily_kgCO2e: totalEmissions
          });
        });
      }
    });

    // Sort first by project_id, then by date chronologically within each project
    return csvRecords.sort((a, b) => {
      if (a.project_id !== b.project_id) {
        return a.project_id.localeCompare(b.project_id);
      }

      // Parse dates for proper chronological sorting within same project
      const dateA = new Date(a.date.replace(/(\w+), (\d+)/, "$2-$1-01"));
      const dateB = new Date(b.date.replace(/(\w+), (\d+)/, "$2-$1-01"));

      return dateA.getTime() - dateB.getTime();
    });
  }
);

// Simple boolean selector to check if we have any carbon emissions data
export const selectHasCarbonEmissionsData = createSelector(
  selectCarbonEmissionsProjects,
  projects => {
    return projects.some(
      project => project.values && project.values.length > 0
    );
  }
);
